import {useState, useMemo, useCallback} from 'react'
import {
  Text,
  Flex,
  ModalHeader,
  ModalBody,
  Box,
  Tag,
  TagLabel,
  Tooltip,
} from '@chakra-ui/react'
import {QuestionOutlineIcon} from '@chakra-ui/icons'
import useSWR from 'swr'
import {PropTypes} from 'prop-types'
import {useLocation} from 'react-router'
import {useTranslation, Trans} from 'react-i18next'

import {BadgeIcon} from '../../../icons/BadgeIcon'
import {fetcher} from '../../../../api/fetcher'
import {camelizeResult} from '../../../../api/serializer'
import {Header, HoveringContinue} from '../components'
import DataTable from '../../DataTable'
import {toCurrency} from '../../../../utils/formatter'
import {columns} from '../components/table/columns'
import {useRails} from '../../../contexts/rails'

const SelectPayersModal = ({
  invitationType,
  goToNextStep,
  goToPreviousStep,
}) => {
  const {t} = useTranslation('invitePayers')
  const {
    organisation: {currentSupplier},
  } = useRails()

  const location = useLocation()
  const supplierId = currentSupplier?.id

  const {data: customerData, isLoading} = useSWR(
    `/api/suppliers/${supplierId}/invite_payers`,
    fetcher,
    {
      use: [camelizeResult],
      revalidateOnFocus: true,
      revalidateOnMount: true,
      revalidateIfStale: true,
      dedupingInterval: 0,
    },
  )

  const customers = useMemo(
    () => customerData?.customers || [],
    [customerData?.customers],
  )
  const [selectedCustomers, setSelectedCustomers] = useState(
    location.state?.selectedCustomers || [],
  )

  const handleToggleSelect = useCallback(
    (customerId) => {
      const customer = customers.find((c) => c.id === customerId)
      if (!customer) return

      setSelectedCustomers((prevSelected) => {
        const isCurrentlySelected = prevSelected.some(
          (c) => c.id === customerId,
        )

        if (invitationType === 'individual') {
          // For individual invitations, only allow one customer to be selected
          return isCurrentlySelected ? [] : [customer]
        }

        // For bulk invitations, allow multiple selections
        return isCurrentlySelected
          ? prevSelected.filter((c) => c.id !== customerId)
          : [...prevSelected, customer]
      })
    },
    [customers, invitationType],
  )

  const handleSelectAll = useCallback(
    (selectAll) => setSelectedCustomers(selectAll ? [...customers] : []),
    [customers],
  )

  const customersWithToggle = useMemo(
    () =>
      customers.map((customer) => {
        const isSelected = selectedCustomers.some((c) => c.id === customer.id)
        return {
          ...customer,
          onToggleSelect: handleToggleSelect,
          rowStyle: isSelected
            ? {
                backgroundColor: 'mld.neutral.200',
              }
            : {},
        }
      }),
    [customers, handleToggleSelect, selectedCustomers],
  )

  if (isLoading) {
    return (
      <>
        <Header
          t={t}
          goToPreviousStep={goToPreviousStep}
          goToPreviousStepEnabled
        />
        <ModalBody display="flex" alignItems="center" justifyContent="center">
          <Text>Loading customers...</Text>
        </ModalBody>
      </>
    )
  }

  const totalAccountsReceivable = selectedCustomers.reduce(
    (sum, customer) => sum + (customer.accountsReceivable?.cents || 0),
    0,
  )

  return (
    <>
      <Header
        t={t}
        goToPreviousStep={goToPreviousStep}
        goToPreviousStepEnabled
      />
      <ModalHeader py={0} px="25px">
        <Flex flexDirection="column" gap={2}>
          <Flex align="center" gap={4} width="100%">
            <Text
              textTransform="uppercase"
              fontSize="sm"
              lineHeight="110%"
              fontWeight="bold"
            >
              {t('modal.steps.selectPayers.title', {
                count: invitationType === 'individual' && 1,
              })}
            </Text>
            <Tooltip
              label={t('modal.steps.selectPayers.yourTopCustomersTooltip', {
                count: customers.length,
              })}
              placement="bottom"
            >
              <Tag variant="status" colorScheme="info" cursor="pointer">
                <BadgeIcon mr={2} boxSize="12px" color="black" />
                <TagLabel>
                  {t('modal.steps.selectPayers.yourTopCustomersLabel', {
                    count: customers.length,
                  })}
                </TagLabel>
              </Tag>
            </Tooltip>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text
              fontSize="xs"
              lineHeight="125%"
              fontWeight="400"
              color="mld.neutral.700"
            >
              {t('modal.steps.selectPayers.description')}
            </Text>
            <Tooltip
              label={
                <Trans
                  t={t}
                  i18nKey="modal.steps.selectPayers.missingCustomerTooltip"
                  components={{bold: <strong />}}
                />
              }
              placement="bottom"
            >
              <Tag
                variant="status"
                fontWeight="500"
                lineHeight="125%"
                colorScheme="info"
                cursor="pointer"
              >
                <TagLabel color="mld.neutral.700">
                  {t('modal.steps.selectPayers.missingCustomerLabel', {
                    count: customers.length,
                  })}
                </TagLabel>
                <QuestionOutlineIcon
                  color="mld.neutral.700"
                  ml={2}
                  boxSize="12px"
                />
              </Tag>
            </Tooltip>
          </Flex>
        </Flex>
      </ModalHeader>
      <ModalBody py="xs">
        {/* Customer table */}
        <Box
          mb={6}
          maxH={selectedCustomers.length > 0 ? '420px' : '480px'}
          overflowY="auto"
          sx={{
            // Header-specific alignment and capitalization
            '& thead': {
              boxShadow: 'none',
              outline: '1px dashed #86827E',
              background: 'white',
            },
            '& thead th': {
              textTransform: 'uppercase',
              fontSize: 'xs',
              fontWeight: 'medium',
              color: 'black',
              paddingY: 'xs',
              paddingX: 0,
              border: 'none',
            },
            // Header and content alignment
            '& thead th, & tbody td': {textAlign: 'left'},
            '& thead th:last-child, & tbody td:last-child': {
              textAlign: 'right',
            }, // Last column right
            '& tbody td': {
              paddingY: '30px',
            },
            // Row border color
            '& tbody tr': {
              borderBottom: '0.5px solid',
              borderColor: 'mld.neutral.500',
            },
            // Custom scrollbar styling
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#c1c1c1',
              borderRadius: '4px',
              '&:hover': {
                background: '#a8a8a8',
              },
            },
          }}
        >
          <DataTable
            columns={columns}
            data={customersWithToggle}
            enableSorting
            showSortIcons
            chakraTableProps={{
              size: 'sm',
              variant: 'unstyled',
              style: {
                tableLayout: 'fixed',
                width: '100%',
              },
            }}
            meta={{
              t,
              selectedCustomers,
              customers,
              onSelectAll: handleSelectAll,
              invitationType,
            }}
          />
        </Box>

        {/* Selected customer summary */}
        {selectedCustomers.length > 0 && (
          <HoveringContinue
            onClick={() =>
              goToNextStep('customize_email_template', {
                selectedCustomers,
                fromStep:
                  invitationType === 'individual'
                    ? 'select_customer'
                    : 'select_customers',
              })
            }
            t={t}
          >
            <Flex justify="space-between" align="center">
              <Flex direction="row">
                <Text fontWeight="regular" fontSize="36px">
                  {invitationType === 'individual'
                    ? selectedCustomers[0]?.name
                    : selectedCustomers.length}
                </Text>
                <Flex px={1} pt={6} gap={3}>
                  <Text fontWeight="regular" fontSize="sm">
                    {invitationType === 'individual'
                      ? 'selected'
                      : `/${customers.length} customers selected`}
                  </Text>
                  <Text fontWeight="medium" fontSize="sm" color="gray.600">
                    {t('modal.steps.selectPayers.accountsReceivables', {
                      amount: toCurrency(totalAccountsReceivable, '$0,0'),
                    })}
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </HoveringContinue>
        )}
      </ModalBody>
    </>
  )
}

SelectPayersModal.propTypes = {
  invitationType: PropTypes.string.isRequired,
  goToNextStep: PropTypes.func.isRequired,
  goToPreviousStep: PropTypes.func.isRequired,
}

export default SelectPayersModal
